import { Alert, Box, Card, Chip, CircularProgress, Collapse, IconButton, Stack, Typography, alpha, useTheme } from '@mui/material';
import { ChevronDown, ChevronRight, FolderGit2, Network, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HierarchyNode, ManagementHierarchy, getManagementHierarchy } from '@/lib/api/managementService';

const LEVEL_COLORS: Record<string, string> = {
  'Management 1': '#6366f1',
  'Management 2': '#3b82f6',
  'Management 3': '#14b8a6',
};

function HierarchyRow({ node, depth }: { node: HierarchyNode; depth: number }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(depth < 2);
  const color = LEVEL_COLORS[node.level] || theme.palette.text.secondary;
  const expandable = node.children.length > 0 || node.projects.length > 0;

  return (
    <Box sx={{ ml: depth === 0 ? 0 : 3, pl: depth === 0 ? 0 : 1.5, borderLeft: depth === 0 ? 'none' : `1px dashed ${alpha(theme.palette.text.primary, 0.2)}` }}>
      <Card
        elevation={0}
        sx={{ border: `1px solid ${alpha(color, 0.35)}`, borderLeft: `4px solid ${color}`, mb: 1, cursor: expandable ? 'pointer' : 'default' }}
        onClick={() => expandable && setOpen((o) => !o)}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.5, py: 1 }}>
          <IconButton size="small" disabled={!expandable} sx={{ visibility: expandable ? 'visible' : 'hidden' }}>
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </IconButton>
          <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: alpha(color, 0.12), display: 'flex' }}>
            <UserRound size={18} color={color} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography fontWeight={600} fontSize="0.9rem" noWrap>{node.email}</Typography>
            <Typography fontSize="0.72rem" color="text.secondary">
              {node.children.length} direct {node.children.length === 1 ? 'report' : 'reports'}
            </Typography>
          </Box>
          <Chip size="small" label={node.level} sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 600 }} />
          <Chip size="small" variant="outlined" icon={<FolderGit2 size={14} />} label={`${node.subtreeProjectCount} ${node.subtreeProjectCount === 1 ? 'project' : 'projects'}`} />
        </Stack>
      </Card>
      <Collapse in={open} unmountOnExit>
        {node.projects.length > 0 && (
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ ml: 5, mb: 1.25 }}>
            {node.projects.map((p) => (
              <Chip
                key={p.appId}
                size="small"
                variant="outlined"
                icon={<FolderGit2 size={13} />}
                label={p.appName}
                onClick={() => navigate(`/projects/${p.appId}`)}
              />
            ))}
          </Stack>
        )}
        {node.children.map((c) => <HierarchyRow key={c.email} node={c} depth={depth + 1} />)}
      </Collapse>
    </Box>
  );
}

export default function ManagementHierarchyPage() {
  const theme = useTheme();
  const [data, setData] = useState<ManagementHierarchy | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getManagementHierarchy()
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.message || 'Failed to load management hierarchy'));
  }, []);

  if (error) return <Stack width="100%" p={3}><Alert severity="error">{error}</Alert></Stack>;
  if (!data) return <Stack width="100%" p={4} alignItems="center"><CircularProgress /></Stack>;

  return (
    <Stack width="100%" p={3} spacing={2.5}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex' }}>
          <Network size={24} color={theme.palette.primary.main} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>Management Hierarchy</Typography>
          <Typography fontSize="0.8rem" color="text.secondary">
            Management 1 sees the projects of Management 2 and 3, Management 2 sees Management 3's, and Management 3 sees only their own.
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1}>
        {Object.entries(LEVEL_COLORS).map(([level, c]) => (
          <Chip key={level} size="small" label={level} sx={{ bgcolor: alpha(c, 0.12), color: c, fontWeight: 600 }} />
        ))}
      </Stack>
      {data.roots.length === 0 ? (
        <Alert severity="info">No reporting lines found. Set "Reports to" on Management 2 and 3 users in a project's Management Access settings.</Alert>
      ) : (
        <Box>{data.roots.map((r) => <HierarchyRow key={r.email} node={r} depth={0} />)}</Box>
      )}
    </Stack>
  );
}
